import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { UnattachedDepositStatus } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { columnFilter, enumOptions, SelectFilterField, TextFilterField } from "@/components/column-filter";
import { StatusBadge } from "@/components/status-badge";
import { Amount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { MerchantCell } from "@/components/merchant-cell";
import { UNATTACHED_DEPOSIT_STATUS_COLOR } from "@/lib/status-colors";
import { type TableSearch, useTableState } from "@/lib/table-state";
import { formatStatusLabel } from "@/lib/utils";
import { listUnattachedDeposits } from "@/api/unattached-deposits";
import { listMerchants } from "@/api/merchants";
import { type ListUnattachedDepositsParams, type UnattachedDepositListItem } from "@/api/types";

// feature-037: every unattached deposit across merchants — deposits sent to
// an invoice's address after the invoice closed. Same list-page shape as
// _app.channel-payments.tsx, with the real internal statuses.
type Filters = { status?: UnattachedDepositStatus; merchantId?: string; search?: string };

const DEFAULT_FILTERS: Filters = {};

function validateSearch(search: Record<string, unknown>): TableSearch<Filters> {
  return {
    page: typeof search.page === "number" ? search.page : undefined,
    pageSize: typeof search.pageSize === "number" ? search.pageSize : undefined,
    sortBy: typeof search.sortBy === "string" ? search.sortBy : undefined,
    sortOrder: search.sortOrder === "asc" || search.sortOrder === "desc" ? search.sortOrder : undefined,
    status: typeof search.status === "string" ? (search.status as UnattachedDepositStatus) : undefined,
    merchantId: typeof search.merchantId === "string" ? search.merchantId : undefined,
    search: typeof search.search === "string" ? search.search : undefined,
  };
}

export const Route = createFileRoute("/_app/unattached-deposits")({
  validateSearch,
  component: UnattachedDepositsPage,
});

function formatDate(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

function RowLink({ id, children }: { id: string; children: ReactNode }) {
  return (
    <Link to="/unattached-deposits/$unattachedDepositId" params={{ unattachedDepositId: id }} className="group -m-2 block p-2">
      {children}
    </Link>
  );
}

function UnattachedDepositsPage() {
  const tableState = useTableState<Filters>({
    defaultFilters: DEFAULT_FILTERS,
    defaultSortBy: "createdAt",
    defaultSortOrder: "desc",
  });
  const params: ListUnattachedDepositsParams = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    ...tableState.filters,
  };
  const { data, isPending, isError } = useQuery({
    queryKey: ["unattached-deposits", params],
    queryFn: () => listUnattachedDeposits(params),
  });
  const { data: merchants } = useQuery({ queryKey: ["merchants"], queryFn: listMerchants });
  const sortedMerchants = [...(merchants ?? [])].sort((a, b) => a.businessName.localeCompare(b.businessName));
  const anyFilterActive = Object.values(tableState.filters).some((v) => v !== undefined);

  const columns: DataTableColumn<UnattachedDepositListItem>[] = [
    {
      key: "publicId",
      header: "Deposit ID",
      filter: columnFilter(tableState, ["search"], (draft, set) => (
        <TextFilterField
          id="filter-search"
          label="Deposit or invoice ID"
          value={draft.search}
          onChange={(v) => set("search", v)}
        />
      )),
      cell: (d) => (
        <RowLink id={d.id}>
          <span className="font-mono group-hover:underline">{d.publicId}</span>
        </RowLink>
      ),
    },
    {
      key: "merchant",
      header: "Merchant",
      filter: columnFilter(tableState, ["merchantId"], (draft, set) => (
        <SelectFilterField
          id="filter-merchant"
          label="Merchant"
          allLabel="All merchants"
          options={sortedMerchants.map((m) => ({ value: m.id, label: m.businessName }))}
          value={draft.merchantId}
          onChange={(v) => set("merchantId", v)}
        />
      )),
      cell: (d) => <MerchantCell merchantId={d.merchant.id} businessName={d.merchant.businessName} publicId={d.merchant.publicId} />,
    },
    {
      key: "invoice",
      header: "Original invoice",
      cell: (d) => (
        <Link to="/invoices/$invoiceId" params={{ invoiceId: d.invoice.id }} className="font-mono text-xs hover:underline">
          {d.invoice.publicId}
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      filter: columnFilter(tableState, ["status"], (draft, set) => (
        <SelectFilterField
          id="filter-status"
          label="Status"
          allLabel="All statuses"
          options={enumOptions(Object.values(UnattachedDepositStatus))}
          value={draft.status}
          onChange={(v) => set("status", v as UnattachedDepositStatus | undefined)}
        />
      )),
      cell: (d) => (
        <RowLink id={d.id}>
          <StatusBadge
            label={formatStatusLabel(d.subStatus ?? d.status)}
            color={UNATTACHED_DEPOSIT_STATUS_COLOR[d.status]}
          />
        </RowLink>
      ),
    },
    {
      key: "asset",
      header: "Asset",
      cell: (d) => (
        <RowLink id={d.id}>
          <AssetLabel symbol={d.asset.symbol} network={d.asset.network} />
        </RowLink>
      ),
    },
    {
      key: "cryptoAmount",
      header: "Amount",
      cell: (d) => (
        <RowLink id={d.id}>
          <Amount native={d.cryptoAmount} symbol={d.asset.symbol} />
        </RowLink>
      ),
    },
    {
      key: "movement",
      header: "Moving",
      cell: (d) => <span className="text-xs text-muted-foreground">{d.activeMovement ? formatStatusLabel(d.activeMovement) : "—"}</span>,
    },
    {
      key: "createdAt",
      header: "Received",
      cell: (d) => <RowLink id={d.id}>{formatDate(d.createdAt)}</RowLink>,
    },
  ];

  return (
    <div className="space-y-6">
      <DataTable
        columns={columns}
        items={data?.data ?? []}
        total={data?.total ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        sortBy={tableState.sortBy}
        sortOrder={tableState.sortOrder}
        loading={isPending}
        error={isError ? "Failed to load unattached deposits." : null}
        onPageChange={(page) => tableState.setPage(page)}
        onPageSizeChange={(pageSize) => tableState.setPageSize(pageSize)}
        onSortChange={(columnKey) => tableState.setSort(columnKey)}
        filterBar={
          anyFilterActive && (
            <Button type="button" variant="ghost" size="sm" onClick={() => tableState.reset()}>
              Clear filters
            </Button>
          )
        }
        getRowKey={(d) => d.id}
        emptyMessage="No unattached deposits match your filters."
      />
    </div>
  );
}
